

// src/services/AdminWebSocketService.js

import { Client } from "@stomp/stompjs";

// // WebSocket URLs
const WS_URL_DELIVERY = "ws://staging.maamaas.com:8080/delivery/ws";
const WS_URL_FOOD = "ws://staging.maamaas.com:8080/food/ws";

// const WS_URL_DELIVERY = "wss://backend.maamaas.com/delivery/ws";
// const WS_URL_FOOD = "wss://backend.maamaas.com/food/ws";


class AdminWebSocketService {
    constructor() {
        // Store multiple clients
        this.clients = new Map(); // key: url, value: client instance
        this.connections = new Map(); // key: url, value: connection status
        this.subscriptions = new Map(); // key: topicPath, value: subscription
        this.adminId = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.isManualDisconnect = false;

        // Topics with their respective WebSocket URLs
        this.availableTopics = [
            {
                name: 'admin-assigned-orders',
                path: '/topic/admin/assigned-orders',
                description: 'Admin assigned orders (Delivery)',
                wsUrl: WS_URL_DELIVERY  // ✅ Delivery WebSocket
            }, 
            {
                name: 'delivery-rejected-offer',
                path: '/topic/reject-offer"',
                description: 'Delivery rejected offer (Delivery)',
                wsUrl: WS_URL_DELIVERY  // ✅ Delivery WebSocket
            },
            {
                name: 'admin-all-orders',
                path: '/topic/admin/all/orders',
                description: 'All admin orders (Food)',
                wsUrl: WS_URL_FOOD  // ✅ Food WebSocket
            }
        ];

        this.topicHandlers = new Map();
    }

    /**
     * Connect to WebSocket and subscribe to all topics
     * @param {string} adminId - Admin ID from login
     */
    connect(adminId) {
        if (!adminId) {
            ////console.error("❌ No admin ID provided for WebSocket connection");
            return;
        }

        this.adminId = adminId;
        this.isManualDisconnect = false;

        ////console.log(`🟢 Connecting WebSockets for admin: ${adminId}`);
        ////console.log(`📍 Delivery WebSocket URL: ${WS_URL_DELIVERY}`);
        //console.log(`📍 Food WebSocket URL: ${WS_URL_FOOD}`);

        const token = localStorage.getItem("authToken");

        // Get unique URLs to connect to
        const uniqueUrls = [...new Set(this.availableTopics.map(t => t.wsUrl))];
        //console.log(`📡 Connecting to ${uniqueUrls.length} WebSocket URLs`);

        // Connect to each unique URL
        uniqueUrls.forEach(url => {
            this.connectToWebSocket(url, adminId, token);
        });
    }

    /**
     * Connect to a specific WebSocket URL
     */
    connectToWebSocket(url, adminId, token) {
        // Check if already connected to this URL
        if (this.clients.has(url) && this.connections.get(url)) {
            //console.log(`✅ WebSocket already connected for ${url}`);
            return;
        }

        //console.log(`🔄 Connecting to WebSocket: ${url}`);

        const client = new Client({
            brokerURL: url,
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            debug: (str) => {
                if (process.env.NODE_ENV !== 'production') {
                    // //console.log("STOMP:", str);
                }
            },
            connectHeaders: {
                'admin-id': adminId,
                'Authorization': `Bearer ${token}`
            }
        });

        client.onConnect = () => {
            //console.log(`✅✅✅ WebSocket Connected Successfully! ${url}`);
            this.connections.set(url, true);
            this.reconnectAttempts = 0;

            // Subscribe to topics for this specific URL
            this.subscribeToTopicsForUrl(url);

            // Store connection info
            localStorage.setItem('admin_websocket_connected', 'true');
            localStorage.setItem('admin_websocket_id', adminId);

            //console.log(`📡 Subscribed to topics for ${url}`);
        };

        client.onStompError = (frame) => {
            //console.error(`❌ STOMP Error (${url}):`, frame.headers["message"]);
            //console.error("Details:", frame.body);
        };

        client.onWebSocketClose = (event) => {
            //console.log(`⚠️ WebSocket Closed (${url}):`, event);
            //console.log(`⚠️ Close Code: ${event.code}`);
            //console.log(`⚠️ Close Reason: ${event.reason || 'No reason provided'}`);
            this.connections.set(url, false);
            localStorage.setItem('admin_websocket_connected', 'false');

            if (this.isManualDisconnect) {
                //console.log(`🔒 Manual disconnect (${url}), auto-reconnect disabled`);
            }
        };

        client.onWebSocketError = (event) => {
            //console.error(`🔴 WebSocket Error (${url}):`, event);
        };

        // Store client
        this.clients.set(url, client);

        // Activate connection
        //console.log(`🔄 Activating STOMP client for ${url}...`);
        client.activate();
        //console.log(`✅ STOMP client activation initiated for ${url}`);
    }

    /**
     * Subscribe to topics for a specific WebSocket URL
     */
    subscribeToTopicsForUrl(url) {
        const topicsForUrl = this.availableTopics.filter(t => t.wsUrl === url);
        const client = this.clients.get(url);

        if (!client || !this.connections.get(url)) {
            //console.error(`❌ Cannot subscribe: WebSocket not connected for ${url}`);
            return;
        }

        //console.log(`🔄 Subscribing to ${topicsForUrl.length} topic(s) for ${url}...`);

        topicsForUrl.forEach((topicConfig, index) => {
            const topicPath = topicConfig.path;

            //console.log(`  📨 [${index + 1}/${topicsForUrl.length}] Subscribing to: ${topicPath}`);
            //console.log(`     📝 Topic: ${topicConfig.name}`);
            //console.log(`     📖 Description: ${topicConfig.description}`);
            //console.log(`     🔗 URL: ${url}`);

            this.subscribeToTopic(client, topicPath, topicConfig.name, url);
        });

        // Store subscribed topics in localStorage
        const subscribedPaths = this.availableTopics.map(t => t.path);
        localStorage.setItem('admin_subscribed_topics', JSON.stringify(subscribedPaths));

        //console.log(`✅ All topics subscribed successfully for ${url}!`);
        //console.log(`📊 Total active subscriptions: ${this.subscriptions.size}`);
    }

    /**
     * Subscribe to a specific topic on a specific client
     */
    subscribeToTopic(client, topicPath, topicName, url) {
        if (!client || !this.connections.get(url)) {
            //console.warn(`⚠️ Cannot subscribe to ${topicPath}: WebSocket not connected for ${url}`);
            return null;
        }

        try {
            //console.log(`🔄 Subscribing to topic: ${topicPath} (${url})`);

            const subscription = client.subscribe(topicPath, (message) => {
                //console.log(`📩 Message arrived on topic: ${topicPath} (${url})`);
                //console.log(`📄 Message body:`, message.body);

                try {
                    let data;
                    try {
                        data = JSON.parse(message.body);
                        //console.log('✅ Message parsed as JSON:', data);
                    } catch (parseError) {
                        //console.log('ℹ️ Message is not JSON, using raw body');
                        data = message.body;
                    }
                    this.handleMessage(topicName, topicPath, data, url);
                } catch (error) {
                    //console.error(`❌ Error handling message for ${topicPath}:`, error);
                }
            });

            // Store subscription with a unique key (topicPath + url)
            const subscriptionKey = `${topicPath}_${url}`;
            this.subscriptions.set(subscriptionKey, subscription);
            //console.log(`✅ Subscribed to topic: ${topicPath} (${url})`);
            //console.log(`📊 Current subscription count: ${this.subscriptions.size}`);
            return subscription;

        } catch (error) {
            //console.error(`❌ Error subscribing to ${topicPath} (${url}):`, error);
            return null;
        }
    }

    /**
     * Handle incoming messages
     */
    handleMessage(topicName, topicPath, message, url) {
        //console.log(`📦 Processing message for ${topicName} (${url})`);
        //console.log(`  📍 Topic Path: ${topicPath}`);
        //console.log(`  📦 Message:`, message);
        //console.log(`  🕐 Received at: ${new Date().toISOString()}`);

        // Store in localStorage
        this.storeMessage(topicName, message);

        // Trigger custom event for components to listen
        const eventDetail = {
            topic: topicName,
            topicPath: topicPath,
            data: message,
            timestamp: new Date().toISOString(),
            wsUrl: url // Include which WebSocket it came from
        };

        //console.log(`📤 Dispatching custom event for ${topicName} (${url})`, eventDetail);
        window.dispatchEvent(new CustomEvent('admin-websocket-message', {
            detail: eventDetail
        }));

        //console.log(`✅ Message processed successfully for ${topicName}`);
    }

    /**
     * Store messages in localStorage
     */
    storeMessage(topicName, message) {
        try {
            const storageKey = `admin_websocket_${topicName}`;

            let existing = [];
            const stored = localStorage.getItem(storageKey);

            if (stored) {
                try {
                    existing = JSON.parse(stored);
                    if (!Array.isArray(existing)) {
                        existing = [];
                    }
                } catch (e) {
                    existing = [];
                }
            }

            const messageWithTime = {
                ...message,
                receivedAt: new Date().toISOString(),
                topic: topicName
            };

            // Keep last 10 messages per topic
            existing.unshift(messageWithTime);
            const toStore = existing.slice(0, 10);
            localStorage.setItem(storageKey, JSON.stringify(toStore));
            localStorage.setItem(`${storageKey}_latest`, JSON.stringify(messageWithTime));

        } catch (error) {
            //console.error(`❌ Error storing message for ${topicName}:`, error);
        }
    }

    /**
     * Get messages for a specific topic
     */
    getMessages(topicName) {
        try {
            const storageKey = `admin_websocket_${topicName}`;
            const messages = JSON.parse(localStorage.getItem(storageKey) || '[]');
            return messages;
        } catch (error) {
            //console.error(`Error getting messages for ${topicName}:`, error);
            return [];
        }
    }

    /**
     * Get latest message for a specific topic
     */
    getLatestMessage(topicName) {
        try {
            const storageKey = `admin_websocket_${topicName}_latest`;
            const message = JSON.parse(localStorage.getItem(storageKey) || 'null');
            return message;
        } catch (error) {
            //console.error(`Error getting latest message for ${topicName}:`, error);
            return null;
        }
    }

    /**
     * Clear messages for a specific topic
     */
    clearMessages(topicName) {
        const storageKey = `admin_websocket_${topicName}`;
        localStorage.removeItem(storageKey);
        localStorage.removeItem(`${storageKey}_latest`);
        //console.log(`🗑️ Cleared messages for ${topicName}`);
    }

    /**
     * Add a new topic at runtime
     */
    addTopic(topicName, path, description = '', wsUrl = WS_URL_DELIVERY) {
        const exists = this.availableTopics.some(t => t.name === topicName);
        if (exists) {
            //console.warn(`⚠️ Topic ${topicName} already exists`);
            return false;
        }

        //console.log(`➕ Adding new topic: ${topicName} (${wsUrl})`);
        this.availableTopics.push({
            name: topicName,
            path: path,
            description,
            wsUrl: wsUrl
        });

        // If already connected to this URL, subscribe immediately
        if (this.connections.get(wsUrl)) {
            const client = this.clients.get(wsUrl);
            if (client) {
                //console.log(`🔄 Subscribing to new topic: ${path} (${wsUrl})`);
                this.subscribeToTopic(client, path, topicName, wsUrl);
            }
        }

        return true;
    }

    /**
     * Remove a topic
     */
    removeTopic(topicName) {
        const index = this.availableTopics.findIndex(t => t.name === topicName);
        if (index === -1) {
            //console.warn(`⚠️ Topic ${topicName} not found`);
            return false;
        }

        //console.log(`🗑️ Removing topic: ${topicName}`);

        const topic = this.availableTopics[index];
        const subscriptionKey = `${topic.path}_${topic.wsUrl}`;
        
        if (this.subscriptions.has(subscriptionKey)) {
            this.unsubscribe(subscriptionKey);
        }

        this.availableTopics.splice(index, 1);
        //console.log(`✅ Removed topic: ${topicName}`);
        return true;
    }

    /**
     * Unsubscribe from a specific topic
     */
    unsubscribe(subscriptionKey) {
        if (this.subscriptions.has(subscriptionKey)) {
            //console.log(`🔓 Unsubscribing from: ${subscriptionKey}`);
            const subscription = this.subscriptions.get(subscriptionKey);
            subscription.unsubscribe();
            this.subscriptions.delete(subscriptionKey);
            //console.log(`✅ Unsubscribed from: ${subscriptionKey}`);
            //console.log(`📊 Remaining subscriptions: ${this.subscriptions.size}`);
        } else {
            //console.warn(`⚠️ No subscription found for: ${subscriptionKey}`);
        }
    }

    /**
     * Disconnect WebSocket (on logout)
     */
    disconnect() {
        //console.log('🔌 Disconnecting Admin WebSockets...');
        //console.log(`👤 Admin ID: ${this.adminId}`);
        //console.log(`📊 Active subscriptions: ${this.subscriptions.size}`);

        // Set manual disconnect flag to prevent auto-reconnect
        this.isManualDisconnect = true;

        // Unsubscribe from all topics
        //console.log(`🔄 Unsubscribing from ${this.subscriptions.size} topics...`);
        this.subscriptions.forEach((subscription, subscriptionKey) => {
            try {
                //console.log(`  🔓 Unsubscribing from: ${subscriptionKey}`);
                subscription.unsubscribe();
                //console.log(`  ✅ Unsubscribed from: ${subscriptionKey}`);
            } catch (error) {
                //console.error(`❌ Error unsubscribing from ${subscriptionKey}:`, error);
            }
        });

        this.subscriptions.clear();
        //console.log('✅ All subscriptions cleared');

        // Deactivate all clients
        this.clients.forEach((client, url) => {
            //console.log(`🔄 Deactivating STOMP client for ${url}...`);
            try {
                client.deactivate();
                //console.log(`✅ STOMP client deactivated for ${url}`);
            } catch (error) {
                //console.error(`❌ Error deactivating client for ${url}:`, error);
            }
        });

        this.clients.clear();
        this.connections.clear();

        this.connected = false;
        this.adminId = null;
        this.reconnectAttempts = 0;

        // Clear localStorage
        localStorage.removeItem('admin_websocket_connected');
        localStorage.removeItem('admin_websocket_id');
        localStorage.removeItem('admin_subscribed_topics');

        //console.log('✅✅✅ All Admin WebSockets disconnected successfully!');
        //console.log('🔒 Auto-reconnect disabled for this session');
    }

    /**
     * Check if connected (to any WebSocket)
     */
    isConnected() {
        // Check if any connection is active
        let anyConnected = false;
        this.connections.forEach((status) => {
            if (status) anyConnected = true;
        });
        //console.log(`🔍 WebSocket Connection Status: ${anyConnected ? 'Connected ✅' : 'Disconnected ❌'}`);
        return anyConnected;
    }

    /**
     * Check connection status for a specific URL
     */
    isConnectedToUrl(url) {
        return this.connections.get(url) || false;
    }

    /**
     * Get connection status details
     */
    getConnectionStatus() {
        const statuses = {};
        this.connections.forEach((status, url) => {
            statuses[url] = status;
        });

        return {
            connected: this.isConnected(),
            adminId: this.adminId,
            subscriptions: this.subscriptions.size,
            topics: this.availableTopics.length,
            reconnectAttempts: this.reconnectAttempts,
            isManualDisconnect: this.isManualDisconnect,
            connections: statuses
        };
    }

    /**
     * Get list of all topics with their status
     */
    getTopics() {
        const topics = this.availableTopics.map(t => {
            const subscriptionKey = `${t.path}_${t.wsUrl}`;
            return {
                ...t,
                currentPath: t.path,
                isSubscribed: this.subscriptions.has(subscriptionKey),
                isConnected: this.connections.get(t.wsUrl) || false
            };
        });

        //console.log('📋 Current Topics Status:', topics);
        return topics;
    }

    /**
     * Force reconnect (useful for manual reconnection attempts)
     */
    reconnect() {
        //console.log('🔄 Manual reconnect requested...');
        if (this.isManualDisconnect) {
            //console.log('⚠️ Manual disconnect flag is set, reconnection disabled');
            return false;
        }

        if (!this.adminId) {
            //console.log('⚠️ No admin ID available for reconnection');
            return false;
        }

        if (this.isConnected()) {
            //console.log('✅ Already connected, no need to reconnect');
            return true;
        }

        //console.log('🔄 Attempting to reconnect...');
        this.connect(this.adminId);
        return true;
    }
}

// Create singleton instance
const adminWebSocketService = new AdminWebSocketService();

// Log initial state
//console.log('🚀 Admin WebSocket Service Initialized');
//console.log('📋 Configuration:');
//console.log(`  📍 Delivery WebSocket URL: ${WS_URL_DELIVERY}`);
//console.log(`  📍 Food WebSocket URL: ${WS_URL_FOOD}`);
//console.log(`  📚 Topics configured: ${adminWebSocketService.availableTopics.length}`);
//console.log(`  📝 Topics: ${adminWebSocketService.availableTopics.map(t => t.name).join(', ')}`);
//console.log(`  🔗 Auto-connect: Enabled on login`);

export default adminWebSocketService;